// REACT
import { useHistory } from "react-router-dom";
import React from "react";

// MATERIAL UI
import { Grid, Typography, styled } from "@mui/material";

// COMPONENTS
import { StyledLink } from "../wallets/Modal/Withdraw/Link";

// ICONS
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "src/icons/portfolio/InfoIcon";

export enum AlertStyleType {
  DEFAULT = "default",
  WARNING = "warning",
  ERROR = "error",
  TRADE = "trade",
}

const StyledAlert = styled(Grid)<{ styleType?: AlertStyleType }>(({ theme, styleType }) => ({
  backgroundColor:
    styleType === AlertStyleType.WARNING
      ? "#FFF3E2"
      : styleType === AlertStyleType.ERROR
      ? "#FFF6F6"
      : "#FEFCF8",
  color: "#000000",
  padding: "12px",
  borderRadius: "8px",
  width: "100%",
  border: styleType === AlertStyleType.TRADE ? "none" : "0.5px solid #002035",
  marginBottom: "24px",
}));

const StyledText = styled(Typography)<{ fontWeight?: number }>(({ theme, fontWeight }) => ({
  fontSize: "14px",
  fontFamily: "Söhne",
  fontWeight: fontWeight,
  color: "#002035",
}));

interface IAlertCard {
  titleText: string;
  subText: string;
  link?: string;
  linkText?: string;
  styleType?: AlertStyleType;
  callback?: () => void;
  id?: string;
  linkPlaceholder?: string;
  links?: string[];
  linkTexts?: string[];
}

/**
 * Card component that displays an alert message to the user.
 *
 * @function
 * @param {string} titleText - The title text of the alert card.
 * @param {string} subText - The subtext of the alert card.
 * @param {string} link - The link to navigate to when the user clicks on the alert card (legacy).
 * @param {string} linkText - The text to display on the link (legacy).
 * @param {string[]} links - Array of links for multiple link support.
 * @param {string[]} linkTexts - Array of link texts corresponding to links.
 * @param {AlertStyleType} styleType - The style type of the alert card.
 * @param {string} linkPlaceholder - Optional placeholder string (e.g., "{{link}}") to specify where links should be embedded.
 * @param {function} callback - Optional callback function for navigation when no links are provided.
 * @returns {JSX.Element} The JSX code to render the AlertCard component.
 * @example
// Old usage (still works)
<AlertCard
  titleText="Important Notice"
  subText="Please review our terms and conditions"
  link="/terms"
  linkText="here"
/>

// New usage with embedded link
<AlertCard
  titleText="Important Notice"
  subText="Please review our {{link}} for more information."
  link="/terms"
  linkText="terms and conditions"
/>

// Custom placeholder
<AlertCard
  titleText="Important Notice"
  subText="Please review our [LINK] for more information."
  link="/terms"
  linkText="terms and conditions"
  linkPlaceholder="[LINK]"
/>

// Multiple links
<AlertCard
  id="withdraw-crypto-modal-no-holdings"
  titleText={"Action Required"}
  subText={
    "It looks like you have no holdings available. Make your first trade {{link}} or deposit your holdings {{link}}."
  }
  links={["/trade", "/deposit"]}
  linkTexts={["here", "here"]}
/>

// Callback-based navigation (no links provided)
<AlertCard
  titleText="Action Required"
  subText="You need to verify your account to access this feature. Verify it here."
  callback={() => handleVerification()}
/>
 */
export const AlertCard = ({
  titleText,
  subText,
  styleType = AlertStyleType.DEFAULT,
  link,
  linkText,
  callback,
  id,
  linkPlaceholder = "{{link}}",
  links,
  linkTexts,
}: IAlertCard): JSX.Element => {
  const history = useHistory();

  const handleLinkClick = (path?: string) => {
    if (path) {
      history.push(path);
    }
    if (callback) {
      callback();
    }
  };

  const handleCallbackClick = () => {
    if (callback) {
      callback();
    }
  };

  const renderSubTextWithLinks = () => {
    // Check if we're using the new multiple links approach
    if (links && linkTexts && links.length > 0 && linkTexts.length > 0) {
      // Count occurrences of the placeholder
      const placeholderCount = (subText.match(new RegExp(linkPlaceholder, "g")) || []).length;

      // If placeholder count doesn't match links count, warn in development
      if (process.env.NODE_ENV === "development") {
        if (placeholderCount !== links.length) {
          console.warn(
            `AlertCard: Number of placeholders (${placeholderCount}) doesn't match number of links (${links.length})`,
          );
        }
        if (links.length !== linkTexts.length) {
          console.warn(
            `AlertCard: Number of links (${links.length}) doesn't match number of link texts (${linkTexts.length})`,
          );
        }
      }

      // Split the text at the placeholder
      const parts = subText.split(linkPlaceholder);

      return (
        <StyledText>
          {parts.map((part, index) => (
            <React.Fragment key={index}>
              {part}
              {index < Math.min(parts.length - 1, links.length, linkTexts.length) && (
                <StyledLink navigateTo={handleLinkClick} path={links[index]}>
                  {linkTexts[index]}
                </StyledLink>
              )}
            </React.Fragment>
          ))}
        </StyledText>
      );
    }
    // Legacy single link support with placeholder
    else if (link && linkText && subText.includes(linkPlaceholder)) {
      const parts = subText.split(linkPlaceholder);

      return (
        <StyledText>
          {parts.map((part, index) => (
            <React.Fragment key={index}>
              {part}
              {index < parts.length - 1 && (
                <StyledLink navigateTo={handleLinkClick} path={link}>
                  {linkText}
                </StyledLink>
              )}
            </React.Fragment>
          ))}
        </StyledText>
      );
    }
    // Original backward compatibility: append link at the end
    else if (link && linkText) {
      return (
        <StyledText>
          {subText}{" "}
          <StyledLink navigateTo={handleLinkClick} path={link}>
            {linkText}
          </StyledLink>
          .
        </StyledText>
      );
    }
    // No links case - check if we have a callback and if the text contains link-like words
    else if (
      callback &&
      (subText.includes("here") || subText.includes("click") || subText.includes("verify"))
    ) {
      // Extract the last word that could be a link (like "here", "verify", etc.)
      const words = subText.split(" ");
      const lastWord = words[words.length - 1];

      // Remove the last word and create the text without it
      const textWithoutLink = words.slice(0, -1).join(" ");

      return (
        <StyledText>
          {textWithoutLink} <StyledLink navigateTo={handleCallbackClick}>{lastWord}</StyledLink>
        </StyledText>
      );
    }
    // No links case - plain text
    else {
      return <StyledText>{subText}</StyledText>;
    }
  };

  const renderIcon = () => {
    if (styleType === AlertStyleType.DEFAULT) {
      return <InfoIcon tooltip={""} />;
    } else if (styleType === AlertStyleType.TRADE) {
      return <ErrorIcon sx={{ height: "16px", width: "16px", marginRight: "8px" }} />;
    } else {
      return <ErrorOutline sx={{ height: "16px", width: "16px", marginRight: "8px" }} />;
    }
  };

  return (
    <StyledAlert styleType={styleType} id={id}>
      <Grid display="flex" alignContent="center" alignItems="center" paddingBottom="8px" gap={1}>
        {renderIcon()}
        <StyledText fontWeight={600}>{titleText}</StyledText>
      </Grid>
      {renderSubTextWithLinks()}
    </StyledAlert>
  );
};